import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useAnnotationStore } from './annotationStore';
import { useCreateAnnotationMutation } from './useAnnotationHooks';
import {
  AnnotationType,
  type AnnotationData,
  type GeometryData,
} from '../../api/protosCompiled/annotation/annotation_pb';
import { useKeyBoardEventStore } from '../../stores/keyEventStore';
import { getAdjustedCursorPosition } from '../svgElements/takeoffHelper';
import { AnnotationInteractionHandlerProps } from './types';
import { getProtoAnnotationType, useGetDefaultStyle, calculateArrowHeadGeometry } from './utils';
import { constants } from './constatnt';
import { getAnnotationMeasurements } from './annotationMeasurements';

export const AnnotationInteractionHandler: React.FC<AnnotationInteractionHandlerProps> = ({
  children,
}) => {
  const {
    isAnnotating,
    activeAnnotationType,
    setIsAnnotating,
    setActiveAnnotationType,
    isHyperlinkAwaitingDestination,
    setHyperlinkAwaitingDestination,
    pendingHyperlinkSource,
    setPendingHyperlinkSource,
    isDimensionalMode,
  } = useAnnotationStore();
  const { selectedObject, cursorPosition, userScale } = useStore(useUnityBuildStore);
  const createAnnotationMutation = useCreateAnnotationMutation();
  const { addOptimisticShape } = useAnnotationStore();
  const { continousMode, snapMode } = useKeyBoardEventStore();
  const { angleOptions } = useStore(useUnityBuildStore);

  const [isDrawing, setIsDrawing] = useState(false);
  const { annotationStartingPoint: startPoint, setAnnotationStartingPoint: setStartPoint } =
    useAnnotationStore();
  const [currentPoint, setCurrentPoint] = useState<{ x: number; y: number } | null>(null);
  const [polylinePoints, setPolylinePoints] = useState<Array<{ x: number; y: number }>>([]);
  const drawingRef = useRef<SVGGElement>(null);

  const getDefaultStyle = useGetDefaultStyle();

  // Create hyperlink annotation function that bypasses state timing issues
  const createHyperlinkAnnotation = useCallback(
    async (position: { x: number; y: number }) => {
      if (!selectedObject) return;

      const geometry: GeometryData.AsObject = {
        x: position.x.toString(),
        y: position.y.toString(),
        text: '',
        width: constants.hyperlink.width,
        height: constants.hyperlink.height,
        cx: '0',
        cy: '0',
        r: '0',
        rx: '0',
        ry: '0',
        x1: '0',
        y1: '0',
        x2: '0',
        y2: '0',
        points: '',
        d: '',
        markerstart: '',
        markerend: '',
        hyperlinkdata: {
          targettype: 0, // OBJECT
          targetobjectid:
            isHyperlinkAwaitingDestination && pendingHyperlinkSource
              ? pendingHyperlinkSource.objectId // Destination link points back to source
              : '', // Source link target will be set when destination is placed
        } as any,
        leadersList: [],
      };

      const annotationType = getProtoAnnotationType('hyperlink');
      const defaultStyle = getDefaultStyle(annotationType);
      const tempId = `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Create optimistic shape immediately
      addOptimisticShape({
        id: tempId,
        objectId: selectedObject.id,
        type: annotationType,
        geometry,
        style: defaultStyle,
      });

      const annotationData = {
        id: tempId,
        objectid: selectedObject.id,
        annotationdata: {
          id: tempId,
          type: annotationType,
          geometry,
          style: defaultStyle,
          transform: undefined,
          layerid: '',
          locked: false,
          visible: true,
          groupid: '',
          selectable: true,
          draggable: true,
          resizable: false,
          custompropertiesMap: [],
          x: position.x.toString(),
          y: position.y.toString(),
          text: '',
          isdimensional: isDimensionalMode,
        } as AnnotationData.AsObject,
        createdbyuserid: '',
        createdbyaccountid: '',
        lastupdated: '',
      };

      try {
        if (!isHyperlinkAwaitingDestination) {
          // First click: just enter destination mode, don't create annotation yet
          setHyperlinkAwaitingDestination(true);
          setPendingHyperlinkSource({
            objectId: selectedObject.id,
            position,
            tempId,
          });
          // Show visual feedback that source is marked
          addOptimisticShape({
            id: tempId,
            objectId: selectedObject.id,
            type: annotationType,
            geometry,
            style: defaultStyle,
          });
          return; // Don't create backend annotation yet
        } else {
          // Second click: create both hyperlinks with correct bidirectional targets

          // Create destination hyperlink pointing back to source
          await createAnnotationMutation.mutateAsync(annotationData);

          // Create source hyperlink pointing to destination
          const sourceGeometry = {
            ...geometry,
            x: pendingHyperlinkSource?.position.x.toString(),
            y: pendingHyperlinkSource?.position.y.toString(),
            hyperlinkdata: {
              targettype: 0,
              targetobjectid: selectedObject?.id || '', // Points to destination
            },
          };

          const sourceAnnotationData = {
            id: `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            objectid: pendingHyperlinkSource?.objectId,
            annotationdata: {
              id: `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: annotationType,
              geometry: sourceGeometry,
              style: defaultStyle,
              transform: undefined,
              layerid: '',
              locked: false,
              visible: true,
              groupid: '',
              selectable: true,
              draggable: true,
              resizable: false,
              custompropertiesMap: [],
              x: pendingHyperlinkSource?.position.x.toString(),
              y: pendingHyperlinkSource?.position.y.toString(),
              text: '',
              isdimensional: isDimensionalMode,
            } as AnnotationData.AsObject,
            createdbyuserid: '',
            createdbyaccountid: '',
            lastupdated: '',
          };

          // Remove the optimistic source shape before creating real one
          const annotationStore = useAnnotationStore.getState();
          annotationStore.removeOptimisticShape(pendingHyperlinkSource?.tempId || '');

          await createAnnotationMutation.mutateAsync(sourceAnnotationData);

          setHyperlinkAwaitingDestination(false);
          setPendingHyperlinkSource(null);
          setActiveAnnotationType(null);
          setIsAnnotating(false);
        }
      } catch (error) {
        console.error('❌ Failed to create hyperlink annotation:', error);
      }
    },
    [
      selectedObject,
      isHyperlinkAwaitingDestination,
      pendingHyperlinkSource,
      getDefaultStyle,
      addOptimisticShape,
      createAnnotationMutation,
      setHyperlinkAwaitingDestination,
      setPendingHyperlinkSource,
      setActiveAnnotationType,
      setIsAnnotating,
      isDimensionalMode,
    ]
  );

  // Notes are created by SVGAnnotations, not here

  // handleMouseDown is defined after createAnnotation to avoid use-before-define

  // Handle mouse move - update drawing
  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!cursorPosition) return;

      // For polyline and polygon tools, show preview with angle snapping
      if (
        (activeAnnotationType === 'polyline' || activeAnnotationType === 'polygon') &&
        isDrawing &&
        polylinePoints.length > 0
      ) {
        let previewPoint = { x: cursorPosition.x, y: cursorPosition.y };

        // Apply angle snapping for preview
        if (snapMode && angleOptions.length > 0) {
          const lastPoint = polylinePoints[polylinePoints.length - 1];
          previewPoint = getAdjustedCursorPosition(
            lastPoint,
            cursorPosition,
            angleOptions,
            snapMode,
            null
          );
        }

        setCurrentPoint(previewPoint);
        return;
      }

      // For line and arrow tools, apply angle snapping if enabled
      if (
        (activeAnnotationType === 'line' || activeAnnotationType === 'arrow') &&
        isDrawing &&
        startPoint
      ) {
        let previewPoint = { x: cursorPosition.x, y: cursorPosition.y };

        // Apply angle snapping for line/arrow preview
        if (snapMode && angleOptions.length > 0) {
          previewPoint = getAdjustedCursorPosition(
            startPoint,
            cursorPosition,
            angleOptions,
            snapMode,
            null
          );
        }

        setCurrentPoint(previewPoint);
        return;
      }

      // For other shape tools
      if (!isDrawing || !startPoint) return;
      setCurrentPoint({ x: cursorPosition.x, y: cursorPosition.y });
    },
    [
      isDrawing,
      startPoint,
      cursorPosition,
      activeAnnotationType,
      polylinePoints,
      snapMode,
      angleOptions,
    ]
  );

  // Create annotation function
  const createAnnotation = useCallback(async () => {
    // For polylines and polygons, we only need polylinePoints, not startPoint/currentPoint
    if (activeAnnotationType === 'polyline' || activeAnnotationType === 'polygon') {
      if (polylinePoints.length < 2 || !activeAnnotationType || !selectedObject) return;
    } else {
      // For other shapes, we need startPoint and currentPoint
      if (!startPoint || !currentPoint || !activeAnnotationType || !selectedObject) return;
    }

    // Set initial geometry based on whether we have startPoint (for most shapes) or polylinePoints (for polylines/polygons)
    const initialX =
      (activeAnnotationType === 'polyline' || activeAnnotationType === 'polygon') &&
      polylinePoints.length > 0
        ? polylinePoints[0].x
        : startPoint?.x || 0;
    const initialY =
      (activeAnnotationType === 'polyline' || activeAnnotationType === 'polygon') &&
      polylinePoints.length > 0
        ? polylinePoints[0].y
        : startPoint?.y || 0;

    const geometry: GeometryData.AsObject = {
      x: initialX.toString(),
      y: initialY.toString(),
      text: '',
      width: '0',
      height: '0',
      cx: '0',
      cy: '0',
      r: '0',
      rx: '0',
      ry: '0',
      x1: '0',
      y1: '0',
      x2: '0',
      y2: '0',
      points: '',
      d: '',
      markerstart: '',
      markerend: '',
      hyperlinkdata: undefined,
      leadersList: [],
    };

    // Set geometry based on annotation type
    switch (activeAnnotationType) {
      case 'text':
        // Create text note as a shape with default dimensions - EXACTLY like rectangles
        if (startPoint && currentPoint) {
          geometry.width = Math.abs(currentPoint.x - startPoint.x).toString();
          geometry.height = Math.abs(currentPoint.y - startPoint.y).toString();
          geometry.x = Math.min(startPoint.x, currentPoint.x).toString();
          geometry.y = Math.min(startPoint.y, currentPoint.y).toString();
          geometry.text = 'Click to edit';
          geometry.leadersList = [];
        }
        break;
      case 'rectangle':
        if (startPoint && currentPoint) {
          geometry.width = Math.abs(currentPoint.x - startPoint.x).toString();
          geometry.height = Math.abs(currentPoint.y - startPoint.y).toString();
          geometry.x = Math.min(startPoint.x, currentPoint.x).toString();
          geometry.y = Math.min(startPoint.y, currentPoint.y).toString();
          geometry.leadersList = [];
        }
        break;
      case 'ellipse':
        if (startPoint && currentPoint) {
          // Create ellipse (or circle if rx = ry)
          const cx = (startPoint.x + currentPoint.x) / 2;
          const cy = (startPoint.y + currentPoint.y) / 2;
          const w = Math.abs(currentPoint.x - startPoint.x);
          const h = Math.abs(currentPoint.y - startPoint.y);
          const rx = w / 2;
          const ry = h / 2;

          geometry.cx = cx.toString();
          geometry.cy = cy.toString();
          geometry.rx = rx.toString();
          geometry.ry = ry.toString();
          // Store bounding box for compatibility
          geometry.x = Math.min(startPoint.x, currentPoint.x).toString();
          geometry.y = Math.min(startPoint.y, currentPoint.y).toString();
          geometry.leadersList = [];
        }
        break;
      case 'line':
        if (startPoint && currentPoint) {
          // Create a simple line with x1,y1,x2,y2 coordinates
          geometry.x1 = startPoint.x.toString();
          geometry.y1 = startPoint.y.toString();
          geometry.x2 = currentPoint.x.toString();
          geometry.y2 = currentPoint.y.toString();
          geometry.leadersList = [];
        }
        break;
      case 'polyline':
        // Create polyline with all collected points
        const pointsString = polylinePoints.map(p => `${p.x},${p.y}`).join(' ');
        geometry.points = pointsString;
        // Set bounding box for compatibility
        if (polylinePoints.length > 0) {
          const xs = polylinePoints.map(p => p.x);
          const ys = polylinePoints.map(p => p.y);
          geometry.x = Math.min(...xs).toString();
          geometry.y = Math.min(...ys).toString();
          geometry.width = (Math.max(...xs) - Math.min(...xs)).toString();
          geometry.height = (Math.max(...ys) - Math.min(...ys)).toString();
          geometry.leadersList = [];
        }
        break;
      case 'polygon':
        // Create polygon with all collected points (same as polyline)
        const polygonPointsString = polylinePoints.map(p => `${p.x},${p.y}`).join(' ');
        geometry.points = polygonPointsString;
        // Set bounding box for compatibility
        if (polylinePoints.length > 0) {
          const xs = polylinePoints.map(p => p.x);
          const ys = polylinePoints.map(p => p.y);
          geometry.x = Math.min(...xs).toString();
          geometry.y = Math.min(...ys).toString();
          geometry.width = (Math.max(...xs) - Math.min(...xs)).toString();
          geometry.height = (Math.max(...ys) - Math.min(...ys)).toString();
          geometry.leadersList = [];
        }
        break;
      case 'arrow':
        if (startPoint && currentPoint) {
          geometry.x1 = startPoint.x.toString();
          geometry.y1 = startPoint.y.toString();
          geometry.x2 = currentPoint.x.toString();
          geometry.y2 = currentPoint.y.toString();
          geometry.leadersList = [];
        }
        break;
      case 'highlighter':
        if (startPoint && currentPoint) {
          const width = Math.abs(currentPoint.x - startPoint.x);
          const height = Math.abs(currentPoint.y - startPoint.y);
          // Ensure minimum size for highlighter (at least 10x10 pixels)
          geometry.width = Math.max(width, 10).toString();
          geometry.height = Math.max(height, 10).toString();
          geometry.x = Math.min(startPoint.x, currentPoint.x).toString();
          geometry.y = Math.min(startPoint.y, currentPoint.y).toString();
          geometry.leadersList = [];
        }
        break;
      case 'hyperlink':
        // Single click placement; in destination mode, store source object id
        if (startPoint && currentPoint) {
          geometry.width = '24';
          geometry.height = '24';
          geometry.x = Math.min(startPoint.x, currentPoint.x).toString();
          geometry.y = Math.min(startPoint.y, currentPoint.y).toString();
          geometry.hyperlinkdata = {
            targettype: 0, // OBJECT
            targetobjectid:
              isHyperlinkAwaitingDestination && pendingHyperlinkSource
                ? pendingHyperlinkSource.objectId // Destination link points back to source
                : '', // Source link target will be set when destination is placed
          } as any;
          geometry.leadersList = [];
        }
        break;
    }

    const annotationType = getProtoAnnotationType(activeAnnotationType);
    const defaultStyle = getDefaultStyle(annotationType);

    // Generate a temporary ID and use it for both optimistic and server create
    const tempId = `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Create optimistic shape immediately
    addOptimisticShape({
      id: tempId,
      objectId: selectedObject.id,
      type: annotationType,
      geometry,
      style: defaultStyle,
    });

    // Create the full annotation structure
    const annotationData = {
      id: tempId,
      objectid: selectedObject.id,
      annotationdata: {
        id: tempId,
        type: annotationType,
        geometry,
        style: defaultStyle,
        transform: undefined,
        layerid: '',
        locked: false,
        visible: true,
        groupid: '',
        selectable: true,
        draggable: true,
        resizable: false,
        // Mark explicitly to render via shape renderer when TEXT
        custompropertiesMap:
          annotationType === AnnotationType.TEXT ? [['render', 'text-shape']] : [],
        // Backward compatibility fields - position only, text stored in geometry
        x: (startPoint?.x || polylinePoints[0]?.x || 0).toString(),
        y: (startPoint?.y || polylinePoints[0]?.y || 0).toString(),
        text: '', // Empty - text only stored in geometry.text
        isdimensional:
          annotationType === AnnotationType.TEXT || annotationType === AnnotationType.HIGHLIGHTER
            ? false
            : isDimensionalMode,
      } as AnnotationData.AsObject,
      createdbyuserid: '',
      createdbyaccountid: '',
      lastupdated: '',
    };

    try {
      // If hyperlink: immediately enter destination mode for instant UX feedback
      if (activeAnnotationType === 'hyperlink') {
        setHyperlinkAwaitingDestination(true);
        setPendingHyperlinkSource({
          objectId: selectedObject.id,
          position: {
            x: Number(geometry.x || startPoint?.x || 0),
            y: Number(geometry.y || startPoint?.y || 0),
          },
          tempId,
        });
      }

      await createAnnotationMutation.mutateAsync(annotationData);
      // Do not remove the optimistic shape here. It will be removed when the real
      // annotation with the same id is fetched and rendered.

      // Always deactivate text annotation tool after creation (no continuous mode for text)
      // For other shapes, respect continuous mode setting
      if (
        activeAnnotationType === 'text' ||
        (!continousMode && activeAnnotationType !== 'hyperlink')
      ) {
        setActiveAnnotationType(null);
        setIsAnnotating(false);
      }
    } catch (error) {
      console.error('Failed to create annotation:', error);
      // On error, we should ideally remove the optimistic shape. Since we don't
      // have access to remove here without importing it, we leave it and expect
      // a retry or manual clean later.
    }
  }, [
    getDefaultStyle,
    startPoint,
    currentPoint,
    activeAnnotationType,
    selectedObject,
    createAnnotationMutation,
    addOptimisticShape,
    continousMode,
    setActiveAnnotationType,
    setIsAnnotating,
    polylinePoints,
    isHyperlinkAwaitingDestination,
    pendingHyperlinkSource,
    setHyperlinkAwaitingDestination,
    setPendingHyperlinkSource,
    isDimensionalMode,
  ]);

  // Handle mouse down - start or progress drawing
  const handleMouseDown = useCallback(
    async (event: React.MouseEvent) => {
      if (!isAnnotating || !activeAnnotationType || !cursorPosition) return;

      // Prevent default behavior
      event.preventDefault();
      event.stopPropagation();

      // For text annotations, use drag-to-define like rectangles
      if (activeAnnotationType === 'text') {
        if (!isDrawing) {
          // First click: start drawing
          setStartPoint({ x: cursorPosition.x, y: cursorPosition.y });
          setCurrentPoint({ x: cursorPosition.x, y: cursorPosition.y });
          setIsDrawing(true);
          return;
        } else {
          // Second click: finalize
          setCurrentPoint({ x: cursorPosition.x, y: cursorPosition.y });
          createAnnotation();
          setIsDrawing(false);
          setStartPoint(null);
          setCurrentPoint(null);
          return;
        }
      }

      // For polyline and polygon tools, each click adds a point with angle snapping
      if (activeAnnotationType === 'polyline' || activeAnnotationType === 'polygon') {
        setIsDrawing(true);

        let pointToAdd = { x: cursorPosition.x, y: cursorPosition.y };

        // Apply angle snapping if there are previous points and snap mode is enabled
        if (polylinePoints.length > 0 && snapMode && angleOptions.length > 0) {
          const lastPoint = polylinePoints[polylinePoints.length - 1];
          pointToAdd = getAdjustedCursorPosition(
            lastPoint,
            cursorPosition,
            angleOptions,
            snapMode,
            null // Not a takeoff assembly
          );
        }

        // For polygons, check if user clicked near the starting point to close the shape
        if (activeAnnotationType === 'polygon' && polylinePoints.length >= 3) {
          const firstPoint = polylinePoints[0];
          const distanceToStart = Math.hypot(
            pointToAdd.x - firstPoint.x,
            pointToAdd.y - firstPoint.y
          );

          // If clicking within 10 pixels of start point, close the polygon
          if (distanceToStart < 10) {
            // Create polygon with current points (don't add the duplicate start point)
            await createAnnotation();
            setIsDrawing(false);
            setPolylinePoints([]);
            setStartPoint(null);
            setCurrentPoint(null);
            return;
          }
        }

        setPolylinePoints(prev => [...prev, pointToAdd]);
        setCurrentPoint(pointToAdd);
        return;
      }

      // For hyperlink: single click placement
      if (activeAnnotationType === 'hyperlink') {
        // Create hyperlink with explicit position parameters to avoid state timing issues
        const hyperlinkPosition = { x: cursorPosition.x, y: cursorPosition.y };
        await createHyperlinkAnnotation(hyperlinkPosition);
        return;
      }

      // For rectangle/circle/arrow: click to start, click again to finish
      if (!isDrawing) {
        // Start drawing on first click
        setIsDrawing(true);
        setStartPoint({ x: cursorPosition.x, y: cursorPosition.y });
        setCurrentPoint({ x: cursorPosition.x, y: cursorPosition.y });
        return;
      } else {
        // Second click finishes the shape
        setCurrentPoint({ x: cursorPosition.x, y: cursorPosition.y });
        await createAnnotation();
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentPoint(null);
        return;
      }
    },
    [
      isAnnotating,
      activeAnnotationType,
      cursorPosition,
      isDrawing,
      createAnnotation,
      polylinePoints,
      snapMode,
      angleOptions,
      createHyperlinkAnnotation,
    ]
  );

  // Handle mouse up - for this UX, we finalize on second click (handled in mouseDown),
  // so mouseUp only updates state for polyline hover preview.
  const handleMouseUp = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  // Handle key presses for canceling
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDrawing) {
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentPoint(null);
        setPolylinePoints([]);
        if (activeAnnotationType === 'hyperlink') {
          setHyperlinkAwaitingDestination(false);
          setPendingHyperlinkSource(null);
          setActiveAnnotationType(null);
          setIsAnnotating(false);
        }
      }
      if (
        event.key === 'Enter' &&
        isDrawing &&
        (activeAnnotationType === 'polyline' || activeAnnotationType === 'polygon')
      ) {
        // Finish polyline/polygon on Enter
        const minPoints = activeAnnotationType === 'polygon' ? 3 : 2; // Polygons need at least 3 points
        if (polylinePoints.length >= minPoints) {
          // Create polyline/polygon with all collected points
          createAnnotation();
        }
        setIsDrawing(false);
        setPolylinePoints([]);
        setStartPoint(null);
        setCurrentPoint(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isDrawing,
    activeAnnotationType,
    polylinePoints,
    currentPoint,
    createAnnotation,
    setHyperlinkAwaitingDestination,
    setPendingHyperlinkSource,
    setActiveAnnotationType,
    setIsAnnotating,
  ]);

  // Helper function to render preview measurements
  const renderPreviewMeasurements = useCallback(
    (type: string, geometry: any) => {
      if (!isDimensionalMode) return null;

      // Text and highlighter annotations should never show measurements
      if (type === 'text' || type === 'highlighter') return null;

      const measurements = getAnnotationMeasurements(type, geometry, {
        precision: 1,
        scale: userScale,
      });

      switch (type) {
        case 'rectangle': {
          const x = parseFloat(geometry.x || '0');
          const y = parseFloat(geometry.y || '0');
          const width = parseFloat(geometry.width || '0');
          const height = parseFloat(geometry.height || '0');

          // Calculate dynamic background sizes
          const widthText = `W ${measurements.width}`;
          const heightText = `H ${measurements.height}`;
          const areaText = `A ${measurements.area}`;
          const perimeterText = `P ${measurements.perimeter}`;

          const widthTextWidth = Math.max(widthText.length * 7, 40);
          const heightTextWidth = Math.max(heightText.length * 7, 40);
          const areaTextWidth = Math.max(areaText.length * 6, 50);
          const perimeterTextWidth = Math.max(perimeterText.length * 6, 50);

          return (
            <g>
              {/* Width measurement with background */}
              <rect
                x={x + width / 2 - widthTextWidth / 2 - 4}
                y={y - 20}
                width={widthTextWidth + 8}
                height="16"
                rx="3"
                ry="3"
                fill="rgba(255,255,255,0.95)"
                stroke="rgba(0,0,0,0.15)"
                strokeWidth="1"
              />
              <text
                x={x + width / 2}
                y={y - 8}
                textAnchor="middle"
                fontSize="12"
                fill="#0066cc"
                fontWeight="500"
              >
                {widthText}
              </text>

              {/* Height measurement with background */}
              <rect
                x={x + width + 2}
                y={y + height / 2 - 8}
                width={heightTextWidth + 8}
                height="16"
                rx="3"
                ry="3"
                fill="rgba(255,255,255,0.95)"
                stroke="rgba(0,0,0,0.15)"
                strokeWidth="1"
              />
              <text
                x={x + width + 6}
                y={y + height / 2 + 2}
                textAnchor="start"
                fontSize="12"
                fill="#0066cc"
                fontWeight="500"
              >
                {heightText}
              </text>

              {/* Area measurement with background */}
              <rect
                x={x + width / 2 - areaTextWidth / 2 - 4}
                y={y + height / 2 - 8}
                width={areaTextWidth + 8}
                height="16"
                rx="3"
                ry="3"
                fill="rgba(255,255,255,0.95)"
                stroke="rgba(0,0,0,0.15)"
                strokeWidth="1"
              />
              <text
                x={x + width / 2}
                y={y + height / 2 + 2}
                textAnchor="middle"
                fontSize="11"
                fill="#0066cc"
                fontWeight="500"
              >
                {areaText}
              </text>

              {/* Perimeter measurement with background */}
              <rect
                x={x + width / 2 - perimeterTextWidth / 2 - 4}
                y={y + height / 2 + 12}
                width={perimeterTextWidth + 8}
                height="16"
                rx="3"
                ry="3"
                fill="rgba(255,255,255,0.95)"
                stroke="rgba(0,0,0,0.15)"
                strokeWidth="1"
              />
              <text
                x={x + width / 2}
                y={y + height / 2 + 22}
                textAnchor="middle"
                fontSize="11"
                fill="#0066cc"
                fontWeight="500"
              >
                {perimeterText}
              </text>
            </g>
          );
        }
        case 'ellipse': {
          const cx = parseFloat(geometry.cx || '0');
          const cy = parseFloat(geometry.cy || '0');
          const rx = parseFloat(geometry.rx || '0');
          const ry = parseFloat(geometry.ry || '0');

          // Position at 45-degree angle from center
          const angle45 = Math.PI / 4;
          const avgRadius = (rx + ry) / 2;
          const offset = avgRadius + 20;
          const anchorX = cx + Math.cos(angle45) * offset;
          const anchorY = cy - Math.sin(angle45) * offset;

          const radiusXText = `Rx ${measurements.radiusX} | Ry ${measurements.radiusY}`;
          const areaText = `A ${measurements.area}`;
          const perimeterText = `P ${measurements.perimeter}`;
          const maxTextLength = Math.max(radiusXText.length, areaText.length, perimeterText.length);
          const labelWidth = Math.max(maxTextLength * 6.5, 80);

          return (
            <g>
              {/* Enhanced background for ellipse measurements */}
              <rect
                x={anchorX - labelWidth / 2 - 6}
                y={anchorY - 18}
                width={labelWidth + 12}
                height="50"
                rx="4"
                ry="4"
                fill="rgba(255,255,255,0.95)"
                stroke="rgba(0,0,0,0.15)"
                strokeWidth="1"
              />
              <text
                x={anchorX}
                y={anchorY - 2}
                textAnchor="middle"
                fontSize="12"
                fill="#0066cc"
                fontWeight="500"
              >
                {radiusXText}
              </text>
              <text
                x={anchorX}
                y={anchorY + 12}
                textAnchor="middle"
                fontSize="11"
                fill="#0066cc"
                fontWeight="500"
              >
                {areaText}
              </text>
              <text
                x={anchorX}
                y={anchorY + 24}
                textAnchor="middle"
                fontSize="11"
                fill="#0066cc"
                fontWeight="500"
              >
                {perimeterText}
              </text>
            </g>
          );
        }
        case 'line':
        case 'arrow': {
          const x1 = parseFloat(geometry.x1 || '0');
          const y1 = parseFloat(geometry.y1 || '0');
          const x2 = parseFloat(geometry.x2 || '0');
          const y2 = parseFloat(geometry.y2 || '0');

          const lengthText = `L ${measurements.length}`;
          const textWidth = Math.max(lengthText.length * 6.5, 40);

          return (
            <g>
              <rect
                x={(x1 + x2) / 2 - textWidth / 2 - 4}
                y={(y1 + y2) / 2 - 8}
                width={textWidth + 8}
                height="16"
                rx="3"
                ry="3"
                fill="rgba(255,255,255,0.95)"
                stroke="rgba(0,0,0,0.15)"
                strokeWidth="1"
              />
              <text
                x={(x1 + x2) / 2}
                y={(y1 + y2) / 2 + 1}
                textAnchor="middle"
                fontSize="12"
                fill="#0066cc"
                fontWeight="500"
              >
                {lengthText}
              </text>
            </g>
          );
        }
        case 'polyline': {
          if (polylinePoints.length < 2) return null;

          // Calculate center of polyline
          const centerX = polylinePoints.reduce((sum, p) => sum + p.x, 0) / polylinePoints.length;
          const centerY = polylinePoints.reduce((sum, p) => sum + p.y, 0) / polylinePoints.length;

          const lengthText = `L ${measurements.length}`;
          const textWidth = Math.max(lengthText.length * 6.5, 40);

          return (
            <g>
              <rect
                x={centerX - textWidth / 2 - 4}
                y={centerY - 8}
                width={textWidth + 8}
                height="16"
                rx="3"
                ry="3"
                fill="rgba(255,255,255,0.95)"
                stroke="rgba(0,0,0,0.15)"
                strokeWidth="1"
              />
              <text
                x={centerX}
                y={centerY + 1}
                textAnchor="middle"
                fontSize="12"
                fill="#0066cc"
                fontWeight="500"
              >
                {lengthText}
              </text>
            </g>
          );
        }
        case 'polygon': {
          if (polylinePoints.length < 3) return null;

          // Calculate center of polygon
          const centerX = polylinePoints.reduce((sum, p) => sum + p.x, 0) / polylinePoints.length;
          const centerY = polylinePoints.reduce((sum, p) => sum + p.y, 0) / polylinePoints.length;

          const perimeterText = `P ${measurements.perimeter}`;
          const textWidth = Math.max(perimeterText.length * 6.5, 40);

          return (
            <g>
              <rect
                x={centerX - textWidth / 2 - 4}
                y={centerY - 8}
                width={textWidth + 8}
                height="16"
                rx="3"
                ry="3"
                fill="rgba(255,255,255,0.95)"
                stroke="rgba(0,0,0,0.15)"
                strokeWidth="1"
              />
              <text
                x={centerX}
                y={centerY + 1}
                textAnchor="middle"
                fontSize="12"
                fill="#0066cc"
                fontWeight="500"
              >
                {perimeterText}
              </text>
            </g>
          );
        }
        default:
          return null;
      }
    },
    [isDimensionalMode, userScale, polylinePoints]
  );

  // Render preview while drawing
  const renderDrawingPreview = () => {
    if (!isDrawing || !activeAnnotationType) return null;

    // For polylines and polygons, we need polylinePoints and currentPoint
    if (activeAnnotationType === 'polyline' || activeAnnotationType === 'polygon') {
      if (polylinePoints.length === 0 || !currentPoint) return null;
    } else {
      // For other shapes, we need startPoint and currentPoint
      if (!startPoint || !currentPoint) return null;
    }

    const props = {
      stroke: '#0066cc',
      strokeWidth: '2',
      fill: 'rgba(0, 102, 204, 0.1)',
      strokeDasharray: '5,5',
    };

    switch (activeAnnotationType) {
      case 'rectangle':
        if (!startPoint || !currentPoint) return null;
        const rectGeometry = {
          x: Math.min(startPoint.x, currentPoint.x).toString(),
          y: Math.min(startPoint.y, currentPoint.y).toString(),
          width: Math.abs(currentPoint.x - startPoint.x).toString(),
          height: Math.abs(currentPoint.y - startPoint.y).toString(),
        };
        return (
          <g>
            <rect
              x={Math.min(startPoint.x, currentPoint.x)}
              y={Math.min(startPoint.y, currentPoint.y)}
              width={Math.abs(currentPoint.x - startPoint.x)}
              height={Math.abs(currentPoint.y - startPoint.y)}
              {...props}
            />
            {renderPreviewMeasurements(activeAnnotationType, rectGeometry)}
          </g>
        );
      case 'highlighter':
        if (!startPoint || !currentPoint) return null;
        return (
          <g>
            <rect
              x={Math.min(startPoint.x, currentPoint.x)}
              y={Math.min(startPoint.y, currentPoint.y)}
              width={Math.abs(currentPoint.x - startPoint.x)}
              height={Math.abs(currentPoint.y - startPoint.y)}
              fill={constants.highlighter.fill}
              stroke={constants.highlighter.stroke}
              strokeWidth={constants.highlighter.strokeWidth}
              opacity={constants.highlighter.opacity}
            />
          </g>
        );
      case 'text':
        if (!startPoint || !currentPoint) return null;
        return (
          <g>
            <rect
              x={Math.min(startPoint.x, currentPoint.x)}
              y={Math.min(startPoint.y, currentPoint.y)}
              width={Math.abs(currentPoint.x - startPoint.x)}
              height={Math.abs(currentPoint.y - startPoint.y)}
              {...props}
            />
          </g>
        );
      case 'ellipse':
        if (!startPoint || !currentPoint) return null;
        const cx = (startPoint.x + currentPoint.x) / 2;
        const cy = (startPoint.y + currentPoint.y) / 2;
        const w = Math.abs(currentPoint.x - startPoint.x);
        const h = Math.abs(currentPoint.y - startPoint.y);
        const rx = w / 2;
        const ry = h / 2;
        const ellipseGeometry = {
          cx: cx.toString(),
          cy: cy.toString(),
          rx: rx.toString(),
          ry: ry.toString(),
        };
        return (
          <g>
            <ellipse cx={cx} cy={cy} rx={rx} ry={ry} {...props} />
            {renderPreviewMeasurements(activeAnnotationType, ellipseGeometry)}
          </g>
        );
      case 'line':
        if (!startPoint || !currentPoint) return null;
        const lineGeometry = {
          x1: startPoint.x.toString(),
          y1: startPoint.y.toString(),
          x2: currentPoint.x.toString(),
          y2: currentPoint.y.toString(),
        };
        return (
          <g>
            <line
              x1={startPoint.x}
              y1={startPoint.y}
              x2={currentPoint.x}
              y2={currentPoint.y}
              {...props}
            />
            {renderPreviewMeasurements(activeAnnotationType, lineGeometry)}
          </g>
        );
      case 'polyline':
        // Polyline preview: existing points + current cursor
        const pts = [...polylinePoints];
        if (currentPoint) pts.push(currentPoint);
        const pointsAttr = pts.map(p => `${p.x},${p.y}`).join(' ');
        const polylineGeometry = {
          points: pointsAttr,
        };
        return (
          <g>
            <polyline points={pointsAttr} {...props} fill="none" />
            {renderPreviewMeasurements(activeAnnotationType, polylineGeometry)}
          </g>
        );
      case 'polygon':
        // Polygon preview: existing points + current cursor + closing line to start
        const polygonPts = [...polylinePoints];
        if (currentPoint) polygonPts.push(currentPoint);

        // Show closing line preview if we have enough points
        if (polygonPts.length >= 3) {
          // Add the closing line back to the start point
          polygonPts.push(polylinePoints[0]);
        }

        const polygonPointsAttr = polygonPts.map(p => `${p.x},${p.y}`).join(' ');
        const polygonGeometry = {
          points: polygonPointsAttr,
        };
        return (
          <g>
            <polygon points={polygonPointsAttr} {...props} />
            {renderPreviewMeasurements(activeAnnotationType, polygonGeometry)}
          </g>
        );
      case 'arrow':
        if (!startPoint || !currentPoint) return null;
        // Calculate arrow head using utility function
        const arrowLength = 15;
        const arrowGeometry = calculateArrowHeadGeometry(
          startPoint.x,
          startPoint.y,
          currentPoint.x,
          currentPoint.y,
          arrowLength
        );
        const { arrowHead1X, arrowHead1Y, arrowHead2X, arrowHead2Y } = arrowGeometry;

        const arrowGeometryData = {
          x1: startPoint.x.toString(),
          y1: startPoint.y.toString(),
          x2: currentPoint.x.toString(),
          y2: currentPoint.y.toString(),
        };
        return (
          <g>
            <line
              x1={startPoint.x}
              y1={startPoint.y}
              x2={currentPoint.x}
              y2={currentPoint.y}
              {...props}
              fill="none"
            />
            <polygon
              points={`${currentPoint.x},${currentPoint.y} ${arrowHead1X},${arrowHead1Y} ${arrowHead2X},${arrowHead2Y}`}
              fill={props.stroke}
              stroke={props.stroke}
            />
            {renderPreviewMeasurements(activeAnnotationType, arrowGeometryData)}
          </g>
        );
      default:
        return null;
    }
  };

  return (
    <g ref={drawingRef}>
      {/* Transparent overlay to capture events for shape creation */}
      {isAnnotating && (
        <rect
          x={0}
          y={0}
          width="100%"
          height="100%"
          fill="transparent"
          pointerEvents="all"
          style={{ cursor: 'crosshair' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        />
      )}
      {children}
      {/* Drawing preview */}
      {renderDrawingPreview()}
    </g>
  );
};

export default AnnotationInteractionHandler;
